import React, { useEffect, useState } from "react";
import { rechargeRequestApiService } from "../../../service/admin/rechargeRequestApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { useLoader } from "../../../base/context/LoaderProvider";
import Pagination from "../../../component/library/pagination";
import moment from "moment";
import { FaCheck, FaTimes, FaMinusCircle, FaFileExport } from "react-icons/fa";
import ExcelTable from "../../../component/library/excelTable";
import { exportToExcel } from "../../../utils/excelExport";
import "../userManagement/style.css";
import "./style.css";

/** Transaction date only (no time) to avoid timezone confusion */
const formatTransactionDate = (date) => (date ? moment(date).format("Do MMM YYYY") : "-");

/** Format history: approved/rejected by email at date time */
const formatHistory = (req) => {
  if (req.status === "pending" || !req.adminActionAt) return "-";
  const email = req.adminId?.email || (req.adminId && typeof req.adminId === "object" ? req.adminId.email : null) || "—";
  const timeStr = moment(req.adminActionAt).format("DD MMM YYYY, HH:mm");
  const action = req.status === "approved" ? "Approved" : "Rejected";
  return `${action} by ${email} on ${timeStr}`;
};

export default function RechargeRequests() {
  const [requests, setRequests] = useState([]);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null); // { id, action: 'approve' | 'reject' }
  const [adminNote, setAdminNote] = useState("");
  const [approvalPassword, setApprovalPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deductModal, setDeductModal] = useState(null); // { request }
  const [walletInfo, setWalletInfo] = useState(null); // { currentBalance, approvalAmount }
  const [deductAmount, setDeductAmount] = useState("");
  const [deductPassword, setDeductPassword] = useState("");
  const [loadingWalletInfo, setLoadingWalletInfo] = useState(false);
  const [submittingDeduct, setSubmittingDeduct] = useState(false);

  const message = useMessage();
  const loader = useLoader();

  const fetchRequests = () => {
    setLoading(true);
    const query = `?page=${page}&limit=${limit}${statusFilter ? `&status=${statusFilter}` : ""}`;
    rechargeRequestApiService
      .getRechargeRequests(query)
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          setRequests(res.data.requests || []);
          setPages({
            currentPage: res.data.currentPage || 1,
            totalPages: res.data.totalPages || 1,
            totalItems: res.data.totalItems || 0,
          });
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Failed to fetch requests", ToastTypes.ERROR);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRequests();
  }, [page, limit, statusFilter]);

  const handleApprove = async () => {
    if (!actionModal?.id) return;
    if (!approvalPassword?.trim()) {
      message.showToast("Admin account password is required", ToastTypes.ERROR);
      return;
    }
    setSubmitting(true);
    loader.showLoading(true);
    rechargeRequestApiService
      .approveRechargeRequest(actionModal.id, adminNote, approvalPassword)
      .then((res) => {
        if (res?.status?.code === 200) {
          message.showToast("Recharge request approved successfully", ToastTypes.SUCCESS);
          setActionModal(null);
          setAdminNote("");
          fetchRequests();
        } else {
          message.showToast(res?.error?.message || "Invalid password or failed to approve", ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Failed to approve", ToastTypes.ERROR);
      })
      .finally(() => {
        setSubmitting(false);
        loader.showLoading(false);
      });
  };

  const handleReject = async () => {
    if (!actionModal?.id) return;
    if (!approvalPassword?.trim()) {
      message.showToast("Admin account password is required", ToastTypes.ERROR);
      return;
    }
    setSubmitting(true);
    loader.showLoading(true);
    rechargeRequestApiService
      .rejectRechargeRequest(actionModal.id, adminNote, approvalPassword)
      .then((res) => {
        if (res?.status?.code === 200) {
          message.showToast("Recharge request rejected", ToastTypes.SUCCESS);
          setActionModal(null);
          setAdminNote("");
          fetchRequests();
        } else {
          message.showToast(res?.error?.message || "Invalid password or failed to reject", ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Failed to reject", ToastTypes.ERROR);
      })
      .finally(() => {
        setSubmitting(false);
        loader.showLoading(false);
      });
  };

  const openActionModal = (req, action) => {
    setActionModal({ id: req._id, action });
    setAdminNote(req.adminNote || "");
    setApprovalPassword("");
  };

  const closeActionModal = () => {
    if (!submitting) {
      setActionModal(null);
      setAdminNote("");
      setApprovalPassword("");
    }
  };

  const openDeductModal = (req) => {
    setDeductModal({ request: req });
    setWalletInfo(null);
    setDeductAmount("");
    setDeductPassword("");
    setLoadingWalletInfo(true);
    rechargeRequestApiService
      .getRechargeWalletInfo(req._id)
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          setWalletInfo({ currentBalance: res.data.currentBalance, approvalAmount: res.data.approvalAmount });
        } else {
          message.showToast(res?.error?.message || "Failed to load wallet info", ToastTypes.ERROR);
        }
      })
      .catch((err) => message.showToast(err?.message || "Failed to load wallet info", ToastTypes.ERROR))
      .finally(() => setLoadingWalletInfo(false));
  };

  const closeDeductModal = () => {
    if (!submittingDeduct) {
      setDeductModal(null);
      setWalletInfo(null);
      setDeductAmount("");
      setDeductPassword("");
    }
  };

  const handleDeductSubmit = () => {
    if (!deductModal?.request?._id) return;
    const amountNum = parseFloat(deductAmount);
    if (!deductAmount || isNaN(amountNum) || amountNum <= 0) {
      message.showToast("Enter a valid amount greater than 0", ToastTypes.ERROR);
      return;
    }
    if (walletInfo && amountNum > walletInfo.currentBalance) {
      message.showToast("Amount cannot exceed current wallet balance", ToastTypes.ERROR);
      return;
    }
    if (!deductPassword?.trim()) {
      message.showToast("Admin account password is required", ToastTypes.ERROR);
      return;
    }
    setSubmittingDeduct(true);
    loader.showLoading(true);
    rechargeRequestApiService
      .deductFromRecharge(deductModal.request._id, amountNum, deductPassword)
      .then((res) => {
        if (res?.status?.code === 200) {
          message.showToast(res.status.message || "Amount deducted successfully", ToastTypes.SUCCESS);
          closeDeductModal();
          fetchRequests();
        } else {
          message.showToast(res?.error?.message || "Failed to deduct", ToastTypes.ERROR);
        }
      })
      .catch((err) => message.showToast(err?.message || "Failed to deduct", ToastTypes.ERROR))
      .finally(() => {
        setSubmittingDeduct(false);
        loader.showLoading(false);
      });
  };

  const getUserName = (user) => {
    if (!user) return "-";
    const first = user.firstName || "";
    const last = user.lastName || "";
    return `${first} ${last}`.trim() || user.email || "-";
  };

  /** Rewrite CloudFront recharge-proof URLs to S3 direct URL so "View Image" works when CloudFront is down. */
  const getPaymentProofDisplayUrl = (url) => {
    if (!url || typeof url !== "string") return url;
    try {
      const u = new URL(url);
      if (u.hostname.endsWith("cloudfront.net") && u.pathname.startsWith("/recharge-proofs/")) {
        return `https://importerr.s3.ap-south-1.amazonaws.com${u.pathname}`;
      }
    } catch (_) {}
    return url;
  };

  /** Export current view (current page requests) to Excel */
  const handleExportToExcel = async () => {
    const exportColumns = [
      { key: "type", label: "Type" },
      { key: "sellerName", label: "Seller Name" },
      { key: "email", label: "Email" },
      { key: "cin", label: "CIN" },
      { key: "senderName", label: "Sender" },
      { key: "amount", label: "Amount" },
      { key: "status", label: "Status" },
      { key: "reason", label: "Reason (Seller)" },
      { key: "transactionId", label: "Transaction ID" },
      { key: "transactionDate", label: "Transaction Date" },
      { key: "history", label: "History" },
      { key: "adminNote", label: "Admin Note" },
    ];
    const exportData = requests.map((req) => ({
      type: req.requestType === "deduct" ? "Deduct" : "Recharge",
      sellerName: req.userName || getUserName(req.userId),
      email: req.userId?.email || "",
      cin: req.cin || "",
      senderName: req.senderName || "",
      amount: req.amount ?? 0,
      status: req.status || "",
      reason: req.message || "",
      transactionId: req.transactionId || "N/A",
      transactionDate: req.transactionDate ? formatTransactionDate(req.transactionDate) : "",
      history: formatHistory(req),
      adminNote: req.adminNote || "",
    }));
    try {
      const result = await exportToExcel(exportData, exportColumns, "recharge-requests");
      if (result?.success) {
        message.showToast("Export completed successfully", ToastTypes.SUCCESS);
      } else {
        message.showToast(result?.error || "Export failed", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Export failed", ToastTypes.ERROR);
    }
  };

  const columns = [
    { key: "requestType", label: "Type", width: 100, render: (_, req) => (
      <span className={`rr-badge rr-badge-type rr-badge-type-${req.requestType || "recharge"}`}>
        {req.requestType === "deduct" ? "Deduct" : "Recharge"}
      </span>
    )},
    { key: "userDetails", label: "User Details", width: 200, render: (_, req) => (
      <div className="rr-user-details">
        <span className="rr-user-name">{req.userName || getUserName(req.userId)}</span>
        <span className="rr-user-email">{req.userId?.email || "-"}</span>
        <span className="rr-user-cin">CIN: {req.cin || "-"}</span>
      </div>
    )},
    { key: "senderName", label: "Sender", width: 120, render: (v) => v || "-" },
    { key: "amount", label: "Amount", width: 120, render: (v) => <span className="rr-amount">₹{(v || 0).toLocaleString()}</span> },
    { key: "paymentProofUrl", label: "Payment Proof", width: 110, render: (url, req) => url ? (
      <a href={getPaymentProofDisplayUrl(url)} target="_blank" rel="noopener noreferrer" className="rr-proof-text" title="View image">View Image</a>
    ) : (
      <span className="rr-proof-na">—</span>
    )},
    { key: "status", label: "Status", width: 100, render: (v) => (
      <span className={`rr-badge rr-badge-${v}`}>{v}</span>
    )},
    { key: "message", label: "Reason (Seller)", width: 180, render: (v) => (
      <span className="rr-reason-text" title={v || ""}>{v || "-"}</span>
    )},
    { key: "transactionId", label: "Transaction", width: 160, render: (_, req) => (
      <div className="rr-transaction-details">
        <span className="rr-txn-id">ID: {req.transactionId || "—"}</span>
        <span className="rr-txn-date">{formatTransactionDate(req.transactionDate)}</span>
      </div>
    )},
    { key: "adminActionAt", label: "History", width: 220, render: (_, req) => (
      <span className="rr-history-text" title={formatHistory(req)}>{formatHistory(req)}</span>
    )},
    { key: "actions", label: "Actions / Response", width: 240, render: (_, req) => (
      <div className="rr-actions-cell">
        {req.status === "pending" ? (
          <div className="rr-actions fc-action-btns">
            <button type="button" className="fc-btn rr-btn-approve" onClick={(e) => { e.stopPropagation(); openActionModal(req, "approve"); }}>
              <FaCheck /> Approve
            </button>
            <button type="button" className="fc-btn fc-btn--danger rr-btn-reject" onClick={(e) => { e.stopPropagation(); openActionModal(req, "reject"); }}>
              <FaTimes /> Reject
            </button>
          </div>
        ) : (
          <div className="rr-actions-cell-approved">
            <span className="rr-admin-response-text" title={req.adminNote || ""}>{req.adminNote || "-"}</span>
            {req.status === "approved" && (req.requestType || "recharge") === "recharge" && (
              <button type="button" className="fc-btn rr-btn-deduct" onClick={(e) => { e.stopPropagation(); openDeductModal(req); }} title="Deduct amount from wallet">
                <FaMinusCircle /> Deduct amount
              </button>
            )}
          </div>
        )}
      </div>
    )},
  ];

  const toolbarRightActions = (
    <select
      className="um-input rr-select"
      value={statusFilter}
      onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
    >
      <option value="">All Status</option>
      <option value="pending">Pending</option>
      <option value="approved">Approved</option>
      <option value="rejected">Rejected</option>
    </select>
  );

  const customExportOptions = [
    {
      type: "button",
      label: "Export to Excel (current page)",
      icon: <FaFileExport style={{ marginRight: 6 }} />,
      onClick: handleExportToExcel,
    },
  ];

  return (
    <div className="um-page rr-page">
      <div className="um-table-section">
        <ExcelTable
          isLoading={loading}
          loadingText="Loading recharge requests…"
          data={requests}
          columns={columns}
          showToolbar={true}
          showToolbarExport={true}
          showToolbarFilter={true}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarRightActions={toolbarRightActions}
          customExportOptions={customExportOptions}
          selectable={false}
          sortable={true}
          resizable={true}
          title="Recharge Requests"
        />

        {pages.totalItems > 0 && (
          <div className="um-pagination">
            <div className="um-pagination-info">
              Showing {pages.totalItems === 0 ? 0 : Math.min((page - 1) * limit + 1, pages.totalItems)} to {Math.min(page * limit, pages.totalItems)} of {pages.totalItems} requests
            </div>
            <div className="um-pagination-controls">
              <button className="um-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
              {Array.from({ length: Math.min(5, pages.totalPages) }, (_, i) => {
                let pageNum;
                if (pages.totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= pages.totalPages - 2) pageNum = pages.totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button
                    key={pageNum}
                    className={`um-page-btn ${page === pageNum ? "um-page-btn-active" : ""}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {pages.totalPages > 5 && page < pages.totalPages - 2 && (
                <>
                  <span className="um-page-dots">...</span>
                  <button className="um-page-btn" onClick={() => setPage(pages.totalPages)}>{pages.totalPages}</button>
                </>
              )}
              <button className="um-page-btn" disabled={page >= pages.totalPages} onClick={() => setPage(page + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Deduct amount modal */}
      {deductModal && (
        <div className="rr-modal-overlay" onClick={closeDeductModal}>
          <div className="rr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rr-modal-header">
              <h3>Deduct amount</h3>
              <button type="button" className="rr-modal-close" onClick={closeDeductModal}>
                <FaTimes />
              </button>
            </div>
            <div className="rr-modal-body">
              {loadingWalletInfo ? (
                <div className="rr-deduct-loading">Loading wallet info…</div>
              ) : walletInfo ? (
                <>
                  <div className="rr-deduct-info">
                    <div className="rr-deduct-row">
                      <span className="rr-deduct-label">Account current wallet balance</span>
                      <span className="rr-deduct-value">₹{Number(walletInfo.currentBalance).toLocaleString()}</span>
                    </div>
                    <div className="rr-deduct-row">
                      <span className="rr-deduct-label">Approval amount (added by admin)</span>
                      <span className="rr-deduct-value">₹{Number(walletInfo.approvalAmount).toLocaleString()}</span>
                    </div>
                  </div>
                  <label className="rr-modal-label">
                    Amount to deduct *
                  </label>
                  <input
                    type="number"
                    className="rr-modal-input"
                    value={deductAmount}
                    onChange={(e) => setDeductAmount(e.target.value)}
                    placeholder="Enter amount"
                    min="1"
                    max={walletInfo.currentBalance}
                    step="1"
                    disabled={submittingDeduct}
                  />
                  <label className="rr-modal-label" style={{ marginTop: "12px" }}>
                    Admin account password *
                  </label>
                  <input
                    type="password"
                    className="rr-modal-input"
                    name="recharge-deduct-password"
                    value={deductPassword}
                    onChange={(e) => setDeductPassword(e.target.value)}
                    placeholder="Enter your admin account password"
                    disabled={submittingDeduct}
                    autoComplete="off"
                  />
                </>
              ) : (
                <div className="rr-deduct-loading">Unable to load wallet info.</div>
              )}
            </div>
            {walletInfo && (
              <div className="rr-modal-footer">
                <button
                  type="button"
                  className="rr-modal-btn rr-modal-btn-cancel"
                  onClick={closeDeductModal}
                  disabled={submittingDeduct}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rr-modal-btn rr-btn-deduct-submit"
                  onClick={handleDeductSubmit}
                  disabled={submittingDeduct || !deductAmount || !deductPassword?.trim()}
                >
                  {submittingDeduct ? "Deducting…" : "Deduct"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Modal (Approve/Reject with note) */}
      {actionModal && (() => {
        const req = requests.find((r) => r._id === actionModal.id);
        const typeLabel = req?.requestType === "deduct" ? "Deduction" : "Recharge";
        return (
        <div className="rr-modal-overlay" onClick={closeActionModal}>
          <div className="rr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rr-modal-header">
              <h3>{actionModal.action === "approve" ? "Approve" : "Reject"} {typeLabel} Request</h3>
              <button type="button" className="rr-modal-close" onClick={closeActionModal}>
                <FaTimes />
              </button>
            </div>
            <div className="rr-modal-body">
              <label className="rr-modal-label">
                Admin account password *
              </label>
              <input
                type="password"
                className="rr-modal-input"
                name="recharge-approval-password"
                value={approvalPassword}
                onChange={(e) => setApprovalPassword(e.target.value)}
                placeholder="Enter your admin account password"
                disabled={submitting}
                autoComplete="off"
              />
              <label className="rr-modal-label" style={{ marginTop: "12px" }}>
                Note to user (optional)
              </label>
              <textarea
                className="rr-modal-textarea"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Add a note for the user..."
                rows={4}
                disabled={submitting}
              />
            </div>
            <div className="rr-modal-footer">
              <button
                type="button"
                className="rr-modal-btn rr-modal-btn-cancel"
                onClick={closeActionModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`rr-modal-btn rr-modal-btn-${actionModal.action}`}
                onClick={actionModal.action === "approve" ? handleApprove : handleReject}
                disabled={submitting}
              >
                {submitting ? "Processing..." : actionModal.action === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

    </div>
  );
}
